import { useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

import logoGala from '@/assets/logo-gala.png';
import logoGalaWhite from '@/assets/logo-gala-white.png';
import logoMarieClaire from '@/assets/logo-marie-claire.png';
import logoMariagesNet from '@/assets/logo-mariages-net.png';
import logoMariezVous from '@/assets/logo-mariez-vous.png';
import logoEPT from '@/assets/logo-evenementiel-pour-tous.png';

const pressLogos = [
  { src: logoGala, srcWhite: logoGalaWhite, alt: 'Magazine Gala', href: 'https://www.moncarnet-gala.fr/articles/view/CYGNATURE-PRODUCTION', className: 'h-20 md:h-20', classNameDark: 'h-20 md:h-22' },
  { src: logoMarieClaire, alt: 'Magazine Marie Claire', href: 'https://www.marieclaire.fr/adresses-incontournables/wedding-events-planner-cygnature-production/', className: 'h-32 md:h-36' },
  { src: logoMariagesNet, alt: 'Mariages.net', href: 'https://www.mariages.net/organisation-mariage/cygnature-production--e311506', className: 'h-32 md:h-36' },
  { src: logoMariezVous, alt: 'Mariez-Vous !', href: 'https://www.mariezvous.fr/annuaire-prestataire-mariage/cygnature-production/', className: 'h-28 md:h-36' },
  { src: logoEPT, alt: 'Evenementiel pour tous — Event Awards', href: 'https://www.evenementielpourtous.com/cygnature-production-126315', className: 'h-20 md:h-36' },
];

interface PressCarouselProps {
  variant?: 'default' | 'dark';
}

const PressCarousel = ({ variant = 'default' }: PressCarouselProps) => {
  const isMobile = useIsMobile();
  const perPage = isMobile ? 3 : 5;
  const totalPages = Math.ceil(pressLogos.length / perPage);
  const [page, setPage] = useState(0);
  const prev = useCallback(() => setPage((p) => (p - 1 + totalPages) % totalPages), [totalPages]);
  const next = useCallback(() => setPage((p) => (p + 1) % totalPages), [totalPages]);
  const cappedPage = Math.min(page, totalPages - 1);
  const visible = pressLogos.slice(cappedPage * perPage, cappedPage * perPage + perPage);

  const getLogoSrc = (logo: typeof pressLogos[0]) => {
    if (variant === 'dark' && logo.srcWhite) return logo.srcWhite;
    return logo.src;
  };

  const getLogoClass = (logo: typeof pressLogos[0]) => {
    if (variant === 'dark' && logo.srcWhite) return 'brightness-100 invert-0';
    return '';
  };

  const getSizeClass = (logo: typeof pressLogos[0]) => {
    if (variant === 'dark' && logo.classNameDark) return logo.classNameDark;
    return logo.className;
  };

  if (!isMobile) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-14">
        {pressLogos.map((logo) => (
          <a key={logo.alt} href={logo.href} target="_blank" rel="noopener noreferrer" className="hover:scale-105 transition-all">
            <img src={getLogoSrc(logo)} alt={logo.alt} className={`${getSizeClass(logo)} w-auto object-contain ${getLogoClass(logo)}`} loading="lazy" />
          </a>
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-center gap-3">
        <button onClick={prev} aria-label="Précédent" className="shrink-0 w-9 h-9 rounded-full border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors">
          <ChevronLeft size={16} />
        </button>
        <div className="flex items-center justify-center gap-4 min-h-[4rem] w-full">
          {visible.map((logo) => (
            <a key={logo.alt} href={logo.href} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center min-w-0 hover:scale-105 transition-all">
              <img src={getLogoSrc(logo)} alt={logo.alt} className={`${getSizeClass(logo)} w-auto max-w-full object-contain ${getLogoClass(logo)}`} loading="lazy" />
            </a>
          ))}
        </div>
        <button onClick={next} aria-label="Suivant" className="shrink-0 w-9 h-9 rounded-full border border-border bg-background flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/30 transition-colors">
          <ChevronRight size={16} />
        </button>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1.5 mt-2 mb-3">
          {Array.from({ length: totalPages }).map((_, i) => (
            <button key={i} onClick={() => setPage(i)} className={`w-2 h-2 rounded-full transition-colors ${i === cappedPage ? 'bg-primary' : 'bg-border'}`} aria-label={`Page ${i + 1}`} />
          ))}
        </div>
      )}
    </div>
  );
};

export default PressCarousel;
